/**
 * Custom cart drawer behaviour: quantity-tier progress bar, inline
 * variant switching on cart lines, discount summary resync and a
 * horizontal "you may also like" upsell row. Layered on top of the
 * theme's native drawer (open/close, remove links, native add-to-cart)
 * without touching that logic.
 */
(function () {
  'use strict';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '{{amount}}';

    function defaultTo(v, d) { return v == null || v !== v ? d : v; }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';

    switch (key) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ','); break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  var drawer = document.getElementById('CartDrawer');
  if (!drawer) return;

  var productsEl = drawer.querySelector('[data-products]');
  var discountsEl = drawer.querySelector('[data-discounts]');
  var checkoutTotalEl = drawer.querySelector('[data-mc-checkout-total]');
  var upsellWrap = drawer.querySelector('[data-mc-upsell]');
  var upsellTrack = drawer.querySelector('[data-mc-upsell-track]');
  var upsellPrevBtn = drawer.querySelector('[data-mc-upsell-prev]');
  var upsellNextBtn = drawer.querySelector('[data-mc-upsell-next]');
  var progressEl = drawer.querySelector('[data-mc-progress]');
  var bubble = document.querySelector('.cart-link__bubble');

  var cartRoute = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart.js';
  var cartAddRoute = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
  var cartChangeRoute = (window.theme && window.theme.routes && window.theme.routes.cartChange) || '/cart/change.js';
  var cartPageRoute = (window.theme && window.theme.routes && window.theme.routes.cartPage) || '/cart';

  var syncing = false;
  var lastUpsellProductId = null;

  // Tier thresholds come from the Liquid snippet, so the discount
  // configuration lives in exactly one place.
  var tiers = [];
  if (progressEl) {
    tiers = (progressEl.getAttribute('data-mc-tiers') || '')
      .split(',')
      .map(function (chunk) {
        var parts = chunk.split(':');
        return { qty: parseInt(parts[0], 10), pct: parseInt(parts[1], 10) };
      })
      .filter(function (t) { return t.qty > 0; })
      .sort(function (a, b) { return a.qty - b.qty; });
  }

  // Cart reads must never come from the HTTP cache: a stale /cart?view=ajax
  // response hands back line item keys that no longer exist, and every
  // remove/quantity call keyed off them then silently does nothing.
  function fetchCart() {
    return fetch(cartRoute, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  function syncBubble(cart) {
    if (!bubble) return;
    bubble.classList.toggle('cart-link__bubble--visible', cart.item_count > 0);
  }

  function syncCheckoutTotal(cart) {
    if (!checkoutTotalEl) return;
    checkoutTotalEl.textContent = formatMoney(cart.total_price);
  }

  function articles(n) { return n === 1 ? '1 article' : n + ' articles'; }

  // Checkpoints sit at evenly spaced positions, so the fill interpolates
  // between them rather than tracking raw quantity — that keeps the bar
  // lined up with the dots whatever the thresholds are.
  function fillPercent(count) {
    if (!tiers.length || count <= 0) return 0;
    var prevQty = 0;
    var prevPos = 0;
    for (var i = 0; i < tiers.length; i++) {
      var pos = ((i + 1) / tiers.length) * 100;
      if (count < tiers[i].qty) {
        var span = tiers[i].qty - prevQty;
        var frac = span > 0 ? (count - prevQty) / span : 1;
        return prevPos + frac * (pos - prevPos);
      }
      prevQty = tiers[i].qty;
      prevPos = pos;
    }
    return 100;
  }

  function updateProgress(cart) {
    if (!progressEl || !tiers.length) return;

    var count = cart.item_count;
    progressEl.hidden = count === 0;
    if (count === 0) return;

    var fill = progressEl.querySelector('[data-mc-progress-fill]');
    if (fill) fill.style.width = fillPercent(count) + '%';

    tiers.forEach(function (tier) {
      var reached = count >= tier.qty;
      var point = progressEl.querySelector('[data-mc-progress-point="' + tier.qty + '"]');
      var label = progressEl.querySelector('[data-mc-progress-label="' + tier.qty + '"]');
      if (point) point.classList.toggle('is-reached', reached);
      if (label) label.classList.toggle('is-reached', reached);
    });

    var unlocked = null;
    var next = null;
    tiers.forEach(function (tier) {
      if (count >= tier.qty) unlocked = tier;
      else if (!next) next = tier;
    });

    var message = progressEl.querySelector('[data-mc-progress-message]');
    if (!message) return;

    if (!next) {
      message.innerHTML = 'Bravo ! Vous avez débloqué <strong>' + unlocked.pct + ' % de remise</strong>';
    } else if (!unlocked) {
      message.innerHTML = 'Ajoutez <strong>' + articles(next.qty - count) + '</strong> et obtenez <strong>' + next.pct + ' % de remise</strong>';
    } else {
      message.innerHTML = '<strong>' + unlocked.pct + ' % de remise</strong> débloqué ! Ajoutez <strong>' +
        articles(next.qty - count) + ' de plus</strong> et obtenez <strong>' + next.pct + ' % de remise</strong>';
    }
  }

  function refreshProductsMarkup() {
    return fetch(cartPageRoute + '?view=ajax', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshItems = doc.querySelector('.cart__items');
        if (freshItems && productsEl) productsEl.innerHTML = freshItems.outerHTML;
        var freshDiscounts = doc.querySelector('.cart__discounts');
        if (discountsEl) discountsEl.innerHTML = freshDiscounts ? freshDiscounts.outerHTML : '';
        primeVariantSelects();
      });
  }

  // Remember each picker's current value so a switch to a combination
  // that has no available variant can be rolled back cleanly, and so the
  // outgoing variant can still be identified after the swap.
  function primeVariantSelects() {
    if (!productsEl) return;
    Array.prototype.forEach.call(
      productsEl.querySelectorAll('select[data-mc-option-index]'),
      function (select) { select.setAttribute('data-mc-prev', select.value); }
    );
  }

  function addToCart(id, quantity) {
    return fetch(cartAddRoute, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  function changeLineQuantity(key, qty) {
    return fetch(cartChangeRoute, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
  }

  function findVariant(variants, values) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v.o || v.o.length !== values.length) continue;
      var same = true;
      for (var j = 0; j < values.length; j++) {
        if (v.o[j] !== values[j]) { same = false; break; }
      }
      if (same) return v;
    }
    return null;
  }

  // Switching colour/size means swapping the line for a different variant.
  // Add the new one first, then drop the old line: if the add fails the
  // shopper still has their original item instead of an emptied row. The
  // outgoing line is looked up in a freshly fetched cart by variant id
  // rather than trusting the rendered data-key, which can lag behind.
  if (productsEl) {
    productsEl.addEventListener('change', function (e) {
      var select = e.target.closest ? e.target.closest('select[data-mc-option-index]') : null;
      if (!select) return;

      var itemEl = select.closest('.cart__item');
      var dataEl = itemEl && itemEl.querySelector('[data-mc-variants]');
      if (!itemEl || !dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var selects = Array.prototype.slice.call(itemEl.querySelectorAll('select[data-mc-option-index]'));
      var chosen = selects.map(function (s) { return s.value; });
      var previous = selects.map(function (s) { return s.getAttribute('data-mc-prev') || s.value; });

      var match = findVariant(variants, chosen);
      if (!match || !match.a) {
        select.value = select.getAttribute('data-mc-prev') || select.value;
        return;
      }

      var oldVariant = findVariant(variants, previous);
      if (oldVariant && oldVariant.id === match.id) return;

      var fallbackKey = itemEl.getAttribute('data-key');
      var qtyInput = itemEl.querySelector('.js-qty__num');
      var qty = (qtyInput ? parseInt(qtyInput.value, 10) : 1) || 1;

      selects.forEach(function (s) { s.disabled = true; });

      addToCart(match.id, qty)
        .then(function () { return fetchCart(); })
        .then(function (cart) {
          var target = null;
          if (oldVariant) {
            for (var i = 0; i < cart.items.length; i++) {
              if (cart.items[i].id === oldVariant.id) { target = cart.items[i]; break; }
            }
          }
          return changeLineQuantity(target ? target.key : fallbackKey, 0);
        })
        .then(function () { return runFullSync(); })
        .catch(function () {
          select.value = select.getAttribute('data-mc-prev') || select.value;
          selects.forEach(function (s) { s.disabled = false; });
        });
    });
  }

  // The theme's own qty-stepper/remove handling (theme.CartForm /
  // theme.QtySelector) only re-binds itself after a full cart re-render,
  // and that re-render aborts partway through in this drawer layout —
  // which silently leaves freshly-added lines with no working +/- at all.
  // Handling quantity + remove ourselves (delegated, capture-phase so the
  // theme's own per-button listener never also fires) sidesteps that
  // entirely and always works, no matter what the native chain does.
  if (productsEl) {
    productsEl.addEventListener('click', function (e) {
      var itemEl = e.target.closest('.cart__item');
      if (!itemEl || !productsEl.contains(itemEl)) return;
      var key = itemEl.getAttribute('data-key');
      if (!key) return;

      var removeLink = e.target.closest('.cart__remove a');
      if (removeLink) {
        e.preventDefault();
        e.stopPropagation();
        changeLineQuantity(key, 0).then(function () { return runFullSync(); });
        return;
      }

      var minusBtn = e.target.closest('.js-qty__adjust--minus');
      var plusBtn = e.target.closest('.js-qty__adjust--plus');
      if (!minusBtn && !plusBtn) return;
      e.preventDefault();
      e.stopPropagation();

      var input = itemEl.querySelector('.js-qty__num');
      var qty = Math.max(0, ((input ? parseInt(input.value, 10) : 1) || 1) + (plusBtn ? 1 : -1));
      if (input) input.value = qty;
      changeLineQuantity(key, qty).then(function () { return runFullSync(); });
    }, true);
  }

  function renderUpsellCard(product) {
    var variant = product.variants && product.variants[0];
    var card = document.createElement('div');
    card.className = 'mc-upsell-card';
    card.dataset.qty = '1';

    var img = product.featured_image || (product.images && product.images[0]) || '';
    var url = escapeHtml(product.url);
    var title = escapeHtml(product.title);

    var priceHtml = '';
    if (variant) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml = '<span class="compare">' + formatMoney(variant.compare_at_price) + '</span>' +
                    '<span>' + formatMoney(variant.price) + '</span>';
      } else {
        priceHtml = '<span>' + formatMoney(variant.price) + '</span>';
      }
    }

    var options = (product.variants || [])
      .filter(function (v) { return v.available; })
      .map(function (v) { return '<option value="' + v.id + '">' + escapeHtml(v.title) + '</option>'; })
      .join('');

    var selectHtml = (product.variants && product.variants.length > 1)
      ? '<select class="mc-upsell-card__variant" aria-label="Choisir une variante">' + options + '</select>'
      : '';

    card.innerHTML =
      '<a href="' + url + '"><img class="mc-upsell-card__image" src="' + escapeHtml(img) + '&width=180" alt="' + title + '" loading="lazy"></a>' +
      '<div class="mc-upsell-card__info">' +
        '<a href="' + url + '"><p class="mc-upsell-card__title">' + title + '</p></a>' +
        '<div class="mc-upsell-card__price">' + priceHtml + '</div>' +
        '<div class="mc-upsell-card__pickers">' +
          selectHtml +
          '<div class="mc-upsell-card__qty">' +
            '<button type="button" class="mc-upsell-card__qty-btn mc-upsell-card__qty-minus" aria-label="Diminuer la quantité">&minus;</button>' +
            '<span class="mc-upsell-card__qty-value">1</span>' +
            '<button type="button" class="mc-upsell-card__qty-btn mc-upsell-card__qty-plus" aria-label="Augmenter la quantité">+</button>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="mc-upsell-card__add">Ajouter</button>';

    // One delegated listener per card — also catches clicks that land on
    // an icon inside a button, via closest('button').
    card.addEventListener('click', function (e) {
      var target = e.target.closest('button');
      if (!target || !card.contains(target)) return;
      e.preventDefault();

      var qtyValueEl = card.querySelector('.mc-upsell-card__qty-value');
      var qty = parseInt(card.dataset.qty, 10) || 1;

      if (target.classList.contains('mc-upsell-card__qty-minus')) {
        qty = Math.max(1, qty - 1);
        card.dataset.qty = String(qty);
        qtyValueEl.textContent = qty;
        return;
      }

      if (target.classList.contains('mc-upsell-card__qty-plus')) {
        qty += 1;
        card.dataset.qty = String(qty);
        qtyValueEl.textContent = qty;
        return;
      }

      if (target.classList.contains('mc-upsell-card__add')) {
        var select = card.querySelector('.mc-upsell-card__variant');
        var id = select ? select.value : variant && variant.id;
        if (!id) return;
        target.disabled = true;
        addToCart(id, qty)
          .then(function (addedLine) {
            card.dataset.qty = '1';
            qtyValueEl.textContent = '1';
            return runFullSync({ moveToTopKey: addedLine && addedLine.key });
          })
          .finally(function () { target.disabled = false; });
      }
    });

    return card;
  }

  function moveItemToTop(key) {
    if (!key || !productsEl) return;
    var list = productsEl.querySelector('.cart__items');
    if (!list) return;
    var item = list.querySelector('[data-key="' + key + '"]');
    if (item && list.firstElementChild !== item) {
      list.insertBefore(item, list.firstElementChild);
      item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function scrollUpsell(direction) {
    if (!upsellTrack) return;
    var card = upsellTrack.querySelector('.mc-upsell-card');
    var step = card ? card.getBoundingClientRect().width + 10 : upsellTrack.clientWidth;
    upsellTrack.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  if (upsellPrevBtn) upsellPrevBtn.addEventListener('click', function () { scrollUpsell(-1); });
  if (upsellNextBtn) upsellNextBtn.addEventListener('click', function () { scrollUpsell(1); });

  function loadUpsell(cart) {
    if (!upsellWrap || !upsellTrack) return Promise.resolve();
    if (!cart.items.length) {
      upsellWrap.hidden = true;
      return Promise.resolve();
    }

    var reference = cart.items[0];
    if (!reference || lastUpsellProductId === reference.product_id) return Promise.resolve();

    return fetch('/recommendations/products.json?product_id=' + reference.product_id + '&limit=10&intent=complementary')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data && data.products) || [];
        if (!products.length) {
          return fetch('/recommendations/products.json?product_id=' + reference.product_id + '&limit=10&intent=related')
            .then(function (r) { return r.json(); })
            .then(function (d2) { return (d2 && d2.products) || []; });
        }
        return products;
      })
      .then(function (products) {
        lastUpsellProductId = reference.product_id;
        upsellTrack.innerHTML = '';
        if (!products.length) {
          upsellWrap.hidden = true;
          return;
        }
        products.forEach(function (p) { upsellTrack.appendChild(renderUpsellCard(p)); });
        upsellWrap.hidden = false;
      })
      .catch(function () {});
  }

  function runFullSync(opts) {
    opts = opts || {};
    if (syncing) return Promise.resolve();
    syncing = true;
    return fetchCart()
      .then(function (cart) {
        syncBubble(cart);
        syncCheckoutTotal(cart);
        updateProgress(cart);
      })
      .then(function () {
        // Always refresh the visible items + discounts — a product added
        // from the upsell row (which doesn't go through the theme's native
        // add-to-cart handler) would otherwise silently update the cart
        // without ever appearing.
        return refreshProductsMarkup();
      })
      .then(function () {
        if (opts.moveToTopKey) moveItemToTop(opts.moveToTopKey);
        return fetchCart();
      })
      .then(function (cart) { return loadUpsell(cart); })
      .finally(function () { syncing = false; });
  }

  // All cart mutations funnel through this single mutex-guarded sync. A
  // mutation arriving mid-flight (e.g. our own refreshProductsMarkup
  // write) schedules exactly one follow-up pass instead of being dropped.
  var pendingResync = false;
  var observer = new MutationObserver(function () {
    if (syncing) {
      pendingResync = true;
      return;
    }
    runFullSync().then(function () {
      if (pendingResync) {
        pendingResync = false;
        runFullSync();
      }
    });
  });

  if (productsEl) observer.observe(productsEl, { childList: true, subtree: false });

  document.addEventListener('DOMContentLoaded', runFullSync);
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    runFullSync();
  }
})();